/**
 * e-Source form builder AQ Assist (client-side heuristics + server apply).
 * Clinical packs + general intents; server Azure ops applied via applyFormBuilderServerAssist.
 */

import { emptyField } from "./esourceService";
import {
  defaultOptionsForType,
  ensureUniqueFieldName,
  isChoiceWithOptions,
  isLayoutField,
  slugifyFieldName,
} from "./esourceFieldTypes";
import { buildCanvasFromUploadedDocument } from "./esourceAssistDocumentFormBuild";

const STARTER_PACKS = [
  {
    id: "cp05a-accountability",
    match: [
      /cp05a/i,
      /imp\s+accountability\s+log/i,
      /ip\s+received\s+at\s+site/i,
    ],
    code: "CP05A",
    name: "IMP Accountability Log",
    scope: "Pharmacy",
    fields: [
      { type: "section", label: "Study details" },
      { type: "text", label: "Investigator Name", required: true, width: "half" },
      { type: "text", label: "Site No", required: true, width: "half" },
      { type: "text", label: "Sponsor Protocol No", required: true, width: "half" },
      { type: "text", label: "Internal Study Code", required: true, width: "half" },
      { type: "text", label: "Drug name", required: true, width: "half" },
      { type: "text", label: "Storage Location", required: true, width: "half" },
      { type: "section", label: "Accountability log" },
      { type: "instruction", label: "Use Form Studio data table for Received / Dispensed / Returned columns (CP05A)." },
      { type: "date", label: "Date Received", required: true, width: "half" },
      { type: "text", label: "Shipment number", required: true, width: "half" },
      { type: "text", label: "Kit No.", required: true, width: "half" },
      { type: "text", label: "Received by (Initials)", required: true, width: "half" },
      { type: "text", label: "Participant No.", required: false, width: "half" },
      { type: "date", label: "Date Dispensed", required: false, width: "half" },
      { type: "text", label: "Dispensed by (Initials & date)", required: false, width: "half" },
      { type: "text", label: "Checked by (Initials & date)", required: false, width: "half" },
      { type: "date", label: "Date returned by Participant", required: false, width: "half" },
      { type: "text", label: "Amount of IMP returned", required: false, width: "half" },
      { type: "textarea", label: "Comments", required: false },
    ],
  },
  {
    id: "sm02a-temperature",
    match: [
      /sm02a/i,
      /temperature\s+monitoring\s+log/i,
      /make\s*\/?\s*serial\s+number/i,
    ],
    code: "SM02A",
    name: "Temperature Monitoring Log",
    scope: "Pharmacy",
    fields: [
      { type: "section", label: "Location and equipment" },
      { type: "text", label: "Site", required: true, width: "half" },
      { type: "text", label: "Location", required: true, width: "half" },
      { type: "text", label: "Equipment", required: true, width: "half" },
      { type: "text", label: "Make / Serial Number", required: true, width: "half" },
      { type: "section", label: "Temperature readings" },
      { type: "date", label: "Date", required: true, width: "half" },
      { type: "time", label: "Time", required: true, width: "half" },
      { type: "number", label: "Min (°C)", required: true, width: "third" },
      { type: "number", label: "Max (°C)", required: true, width: "third" },
      { type: "number", label: "Actual (°C)", required: true, width: "third" },
      {
        type: "textarea",
        label: "Comments (IP received, IP moved; fridge door left open etc)",
        required: false,
      },
      { type: "text", label: "Staff Signature", required: true },
    ],
  },
  {
    id: "cp03a-sample-processing",
    match: [
      /cp03a/i,
      /biological\s+sample\s+processing/i,
      /time\s+into\s+centrifuge/i,
    ],
    code: "CP03A",
    name: "Biological Sample Processing Log",
    scope: "Lab",
    fields: [
      { type: "section", label: "Study details" },
      { type: "text", label: "Site", required: true, width: "half" },
      { type: "text", label: "Study Code", required: true, width: "half" },
      { type: "text", label: "Protocol number", required: true, width: "half" },
      { type: "text", label: "Sponsor name", required: true, width: "half" },
      { type: "text", label: "Visit", required: true, width: "half" },
      { type: "section", label: "Processing entry" },
      { type: "date", label: "Date", required: true, width: "half" },
      { type: "text", label: "Participant No.", required: true, width: "half" },
      { type: "time", label: "Time samples obtained", required: true, width: "half" },
      {
        type: "text",
        label: "Samples processed as per laboratory manual by (Initials & date)",
        required: true,
        width: "half",
      },
      {
        type: "textarea",
        label: "Comments (issues with processing/storage)",
        required: false,
      },
    ],
  },
  {
    id: "cp07b-compliance",
    match: [
      /cp07b/i,
      /monthly\s+study\s+specific\s+imp\s+compliance/i,
      /quarantined\s+stock/i,
      /accountability\s+checks/i,
    ],
    code: "CP07B",
    name: "Monthly Study Specific IMP Compliance Check",
    scope: "Pharmacy",
    fields: [
      { type: "section", label: "Study details" },
      { type: "text", label: "Study Name", required: true, width: "half" },
      { type: "text", label: "Internal Study Code", required: true, width: "half" },
      { type: "text", label: "Protocol No", required: true, width: "half" },
      { type: "text", label: "Site", required: true, width: "half" },
      { type: "text", label: "Principal Investigator", required: true, width: "half" },
      { type: "text", label: "Site Number", required: true, width: "half" },
      { type: "date", label: "Date of last check", required: true, width: "half" },
      { type: "text", label: "Responsible Person", required: true, width: "half" },
      { type: "section", label: "IMP details" },
      { type: "text", label: "Name of IMP", required: true, width: "half" },
      { type: "text", label: "Storage Requirements", required: true, width: "half" },
      { type: "text", label: "Location", required: true, width: "half" },
      { type: "section", label: "Safety checks" },
      { type: "yesno", label: "Have any IMP kits passed expiry date?", required: true, width: "half" },
      { type: "yesno", label: "Has there been any quarantined stock released?", required: true, width: "half" },
      { type: "section", label: "Accountability checks" },
      {
        type: "yesno",
        label: "QC Individual Subject and Master / Combined Accountability Logs",
        required: true,
        width: "half",
      },
      { type: "yesno", label: "Returned/Used IMP Reconciliation", required: true, width: "half" },
      {
        type: "yesno",
        label: "QC Temperature Monitoring Logs and file copies in PSF",
        required: true,
        width: "half",
      },
      { type: "textarea", label: "Issues Identified and Actions Taken", required: false },
      { type: "text", label: "Completed By (RN / Delegate Name)", required: true, width: "half" },
      { type: "signature", label: "Signature", required: true },
    ],
  },
  {
    id: "vitals",
    match: [/vital\s*signs/i, /blood pressure/i, /\bbp\b/i, /heart\s*rate/i, /pulse\s*ox/i],
    code: "VS01",
    name: "Vital signs",
    scope: "General",
    fields: [
      { type: "section", label: "Measurement" },
      { type: "datetime", label: "Measurement date & time", required: true, width: "half" },
      { type: "integer", label: "Systolic BP (mmHg)", required: true, width: "third" },
      { type: "integer", label: "Diastolic BP (mmHg)", required: true, width: "third" },
      { type: "integer", label: "Heart rate (bpm)", required: true, width: "third" },
      { type: "number", label: "Temperature (°C)", required: true, width: "half" },
      { type: "integer", label: "Respiratory rate", required: false, width: "half" },
      { type: "textarea", label: "Notes", required: false },
    ],
  },
  {
    id: "ae",
    match: [/adverse.?event/i, /\bae\b/i, /safety.?event/i],
    code: "AE01",
    name: "Adverse event",
    scope: "General",
    fields: [
      { type: "section", label: "Event" },
      { type: "text", label: "AE term", required: true, width: "half" },
      { type: "date", label: "Onset date", required: true, width: "half", dontAllowFuture: true },
      {
        type: "radio",
        label: "Severity",
        required: true,
        options: ["Mild", "Moderate", "Severe"],
      },
      {
        type: "dropdown",
        label: "Related to IMP",
        required: true,
        options: ["Not related", "Possibly", "Probably", "Definitely"],
        width: "half",
      },
      {
        type: "dropdown",
        label: "Outcome",
        required: false,
        options: ["Ongoing", "Resolved", "Resolved with sequelae", "Fatal"],
        width: "half",
      },
      { type: "textarea", label: "Description", required: true },
    ],
  },
];

/** Intent packs for everyday / non-clinical forms (AQ Assist free text). */
const GENERAL_INTENTS = [
  {
    id: "order",
    match: [
      /\border\b/i,
      /kirana/i,
      /grocery/i,
      /shop\b/i,
      /store\b/i,
      /customer.?order/i,
      /purchase.?order/i,
      /\bpos\b/i,
      /take.?order/i,
    ],
    nameFrom: (t) => titleCase(clip(t, 60)) || "Customer order form",
    code: "ORD01",
    fields: [
      { type: "section", label: "Customer" },
      { type: "text", label: "Customer name", required: true, width: "half" },
      { type: "text", label: "Phone / mobile", required: true, width: "half" },
      { type: "textarea", label: "Delivery address", required: false },
      { type: "section", label: "Order" },
      { type: "date", label: "Order date", required: true, width: "half", defaultCurrentDate: true },
      { type: "time", label: "Order time", required: false, width: "half" },
      { type: "textarea", label: "Items / products ordered", required: true },
      { type: "integer", label: "Quantity", required: true, width: "half" },
      { type: "number", label: "Amount (₹)", required: false, width: "half" },
      {
        type: "radio",
        label: "Payment mode",
        required: true,
        options: ["Cash", "UPI", "Card", "Credit / later"],
        optionsLayout: "stacked",
      },
      { type: "yesno", label: "Home delivery?", required: false, width: "half" },
      { type: "textarea", label: "Special instructions", required: false },
    ],
  },
  {
    id: "feedback",
    // Avoid bare "review" — that is used for reviewing the canvas form.
    match: [
      /feedback/i,
      /survey/i,
      /rating/i,
      /satisfaction/i,
      /customer.?review/i,
      /product.?review/i,
      /leave.?a.?review/i,
    ],
    nameFrom: (t) => titleCase(clip(t, 60)) || "Feedback form",
    code: "FB01",
    fields: [
      { type: "section", label: "About you" },
      { type: "text", label: "Name", required: false, width: "half" },
      { type: "text", label: "Email / phone", required: false, width: "half" },
      { type: "section", label: "Feedback" },
      { type: "date", label: "Date", required: true, width: "half", defaultCurrentDate: true },
      {
        type: "radio",
        label: "Overall rating",
        required: true,
        options: ["1", "2", "3", "4", "5"],
        optionsLayout: "inline",
      },
      { type: "textarea", label: "What went well?", required: false },
      { type: "textarea", label: "What can we improve?", required: false },
      { type: "yesno", label: "May we contact you?", required: false },
    ],
  },
  {
    id: "registration",
    match: [/registr/i, /sign.?up/i, /enrol/i, /enroll/i, /onboard/i, /admission/i],
    nameFrom: (t) => titleCase(clip(t, 60)) || "Registration form",
    code: "REG01",
    fields: [
      { type: "section", label: "Personal details" },
      { type: "text", label: "Full name", required: true, width: "half" },
      { type: "date", label: "Date of birth", required: false, width: "half" },
      { type: "text", label: "Email", required: true, width: "half" },
      { type: "text", label: "Phone", required: true, width: "half" },
      { type: "textarea", label: "Address", required: false },
      {
        type: "dropdown",
        label: "Gender",
        required: false,
        options: ["Female", "Male", "Other", "Prefer not to say"],
        width: "half",
      },
      { type: "textarea", label: "Notes", required: false },
    ],
  },
  {
    id: "attendance",
    match: [/attendance/i, /check.?in/i, /presence/i, /roll.?call/i],
    nameFrom: (t) => titleCase(clip(t, 60)) || "Attendance form",
    code: "ATT01",
    fields: [
      { type: "date", label: "Date", required: true, width: "half", defaultCurrentDate: true },
      { type: "time", label: "Time", required: false, width: "half" },
      { type: "text", label: "Person name / ID", required: true, width: "half" },
      {
        type: "radio",
        label: "Status",
        required: true,
        options: ["Present", "Absent", "Late", "Leave"],
        optionsLayout: "stacked",
      },
      { type: "textarea", label: "Remarks", required: false },
    ],
  },
  {
    id: "complaint",
    match: [/complaint/i, /grievance/i, /issue\b/i, /ticket\b/i, /incident/i],
    nameFrom: (t) => titleCase(clip(t, 60)) || "Complaint form",
    code: "CMP01",
    fields: [
      { type: "section", label: "Reporter" },
      { type: "text", label: "Name", required: true, width: "half" },
      { type: "text", label: "Contact", required: true, width: "half" },
      { type: "section", label: "Issue" },
      { type: "date", label: "Date reported", required: true, width: "half", defaultCurrentDate: true },
      { type: "text", label: "Subject", required: true },
      {
        type: "dropdown",
        label: "Priority",
        required: true,
        options: ["Low", "Medium", "High", "Urgent"],
        width: "half",
      },
      { type: "textarea", label: "Description", required: true },
      { type: "yesno", label: "Follow-up required?", required: false },
    ],
  },
  {
    id: "inventory",
    match: [/inventory/i, /stock\b/i, /warehouse/i, /goods.?in/i, /goods.?out/i],
    nameFrom: (t) => titleCase(clip(t, 60)) || "Inventory form",
    code: "INV01",
    fields: [
      { type: "date", label: "Date", required: true, width: "half", defaultCurrentDate: true },
      { type: "text", label: "Item name / SKU", required: true, width: "half" },
      { type: "integer", label: "Quantity", required: true, width: "half" },
      {
        type: "radio",
        label: "Movement",
        required: true,
        options: ["In", "Out", "Adjustment"],
        optionsLayout: "inline",
      },
      { type: "text", label: "Location / bin", required: false, width: "half" },
      { type: "textarea", label: "Notes", required: false },
    ],
  },
  {
    id: "contact",
    match: [/contact\b/i, /enquiry/i, /inquiry/i, /lead\b/i, /callback/i],
    nameFrom: (t) => titleCase(clip(t, 60)) || "Contact form",
    code: "CNT01",
    fields: [
      { type: "text", label: "Name", required: true, width: "half" },
      { type: "text", label: "Email", required: true, width: "half" },
      { type: "text", label: "Phone", required: false, width: "half" },
      { type: "text", label: "Subject", required: true },
      { type: "textarea", label: "Message", required: true },
    ],
  },
];

function clip(text, max) {
  const t = String(text || "").trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

function titleCase(text) {
  return String(text || "")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function codeFromTitle(title) {
  const letters = String(title || "")
    .replace(/[^a-zA-Z0-9\s]/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 3)
    .map((w) => w[0].toUpperCase())
    .join("");
  return (letters || "FRM").slice(0, 4) + "01";
}

function fieldFromSpec(spec, existingFields = []) {
  const type = spec.type || "text";
  const base = emptyField(type);
  const label = spec.label || base.label;
  const field = {
    ...base,
    label,
    required: !!spec.required,
    width: spec.width || base.width,
    placeholder: spec.placeholder || "",
    helpText: spec.helpText || "",
    defaultValue: spec.defaultValue ?? base.defaultValue,
    maxLength: spec.maxLength ?? "",
    minLength: spec.minLength ?? "",
    pattern: spec.pattern ?? "",
    minValue: spec.minValue ?? "",
    maxValue: spec.maxValue ?? "",
    dontAllowFuture: !!spec.dontAllowFuture,
    dontAllowPast: !!spec.dontAllowPast,
    defaultCurrentDate: !!spec.defaultCurrentDate,
    optionsLayout: spec.optionsLayout || base.optionsLayout || "stacked",
    options: isChoiceWithOptions(type)
      ? spec.options || defaultOptionsForType(type)
      : undefined,
  };
  field.fieldName = ensureUniqueFieldName(
    slugifyFieldName(label),
    existingFields,
    field.id,
  );
  return field;
}

function mapSpecsWithUniqueNames(specs) {
  const out = [];
  (specs || []).forEach((spec) => {
    out.push(fieldFromSpec(spec, out));
  });
  return out;
}

function inferFieldType(label) {
  const l = String(label || "");
  if (/date\s*&?\s*time|datetime/i.test(l)) return "datetime";
  if (/\btime\b/i.test(l)) return "time";
  if (/\bdate\b|dob|birth/i.test(l)) return "date";
  if (/e-?mail|emailid|email\s*id|mail\s*id/i.test(l)) return "text";
  if (/\b(phone|mobile|tel)\b/i.test(l)) return "text";
  if (/note|comment|description|address|message|instruction|remark|detail/i.test(l))
    return "textarea";
  if (/qty|quantity|count|age|score|rating|integer/i.test(l)) return "integer";
  if (/amount|price|cost|total|number|decimal|₹|rs\.?/i.test(l)) return "number";
  if (/yes\s*\/?\s*no|delivery\?|required\?/i.test(l)) return "yesno";
  if (/checkbox|confirm|agree/i.test(l)) return "checkbox";
  if (/payment|mode|status|priority|gender|type\b/i.test(l)) return "dropdown";
  return "text";
}

function parseInlineFields(prompt) {
  const text = String(prompt || "").trim();
  if (!text) return [];

  const fields = [];
  const listMatch = text.match(/(?:fields?|include|add)\s*:?\s*(.+)$/i);
  if (listMatch) {
    const chunk = listMatch[1];
    const parts = chunk
      .split(/,| and |;/i)
      .map((s) => s.trim())
      .filter((s) => s.length > 1 && s.length < 80);
    for (const label of parts.slice(0, 20)) {
      const type = inferFieldType(label);
      fields.push(
        fieldFromSpec(
          {
            type,
            label: label.replace(/\s*\(required\)\s*/i, "").trim(),
            required: /required/i.test(label),
          },
          fields,
        ),
      );
    }
  }
  return fields;
}

function matchGeneralIntent(text) {
  for (const intent of GENERAL_INTENTS) {
    if (intent.match.some((re) => re.test(text))) return intent;
  }
  return null;
}

function hasCanvasFields(currentFields) {
  return Array.isArray(currentFields) && currentFields.length > 0;
}

function isSmallTalk(text) {
  const t = String(text || "").trim();
  if (!t) return false;
  if (t.length > 60) return false;
  // Whole-message acknowledgements only — not "please remove…"
  return /^(ok|okay|k+|thanks|thank you|thx|got it|will do|sure|yes|yep|no|nope|alright|all right|cool|noted|fine|please|pls|hmm+|i(?:'| a)?m good|i will(?: do)?|ok i will(?: do)?)[.!?]*$/i.test(
    t,
  );
}

function normalizeFieldKey(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

/** User is asking to create / generate a new form (not chat about the canvas). */
function isNewFormBuildRequest(text) {
  const t = String(text || "").trim();
  if (!t) return false;
  return (
    /\b(create|build|generate|make|design)\b.{0,40}\bform\b/i.test(t) ||
    /\bform\b.{0,40}\b(create|build|generate|make)\b/i.test(t) ||
    /\b(give me|i need|i want|set up|setup)\b.{0,40}\bform\b/i.test(t) ||
    /\b(new form|blank form)\b/i.test(t) ||
    /\bform for\b/i.test(t) ||
    /\bform to\b/i.test(t)
  );
}

/** Review / improve / edit an existing canvas form (not a new pack). */
function isCanvasAssistIntent(text) {
  const t = String(text || "").trim();
  if (!t) return false;
  return (
    /\breview\b/i.test(t) ||
    /\bgaps?\b/i.test(t) ||
    /missing/i.test(t) ||
    /improve/i.test(t) ||
    /suggest/i.test(t) ||
    /what.?s wrong/i.test(t) ||
    /analy[sz]e/i.test(t) ||
    /look at (my|this|the) form/i.test(t) ||
    /current form/i.test(t) ||
    /\bcanvas\b/i.test(t) ||
    /\bchange\b/i.test(t) ||
    /\bupdate\b/i.test(t) ||
    /\bmodify\b/i.test(t) ||
    /\bedit\b/i.test(t) ||
    /add\s+(a\s+|an\s+)?field/i.test(t) ||
    /add\s+(a\s+|an\s+)?["']?.+["']?\s+field/i.test(t) ||
    /remove\s+(the\s+|a\s+|an\s+)?field/i.test(t) ||
    /make\s+.+\s+required/i.test(t) ||
    /make\s+.+\s+optional/i.test(t) ||
    /(?:set|mark)\s+.+\s+(?:as\s+)?(?:required|mandatory|optional)/i.test(t) ||
    /\bas\s+required\b/i.test(t) ||
    /\bas\s+optional\b/i.test(t) ||
    /\b(change|convert)\b.+\b(type|text|number|integer|date)\b/i.test(t) ||
    /\b(accept|only|digits?|max\s*length|min\s*length|pattern|validation)\b/i.test(
      t,
    ) ||
    /how many fields/i.test(t) ||
    /what (fields|is) (on|in) (the|this|my) form/i.test(t) ||
    /\bdivid(?:er|ers)?\b|\bdivert\b|\bdivder\b|\bseparator\b/i.test(t) ||
    /\b(insert|put|place)\b.+\b(after|before)\b/i.test(t)
  );
}

function isChangeOrReviewRequest(text) {
  const t = String(text || "").trim();
  if (!t) return false;
  return (
    isCanvasAssistIntent(t) ||
    /\b(change|update|modify|edit|fix|tweak|adjust|rename|replace)\b/i.test(t) ||
    /\b(add|remove|delete)\b/i.test(t) ||
    /\breview\b|\bgaps?\b|improve|suggest/i.test(t)
  );
}

function availableFormsBlurb(allowedModes) {
  const modes = normalizeAllowedModes(allowedModes);
  const parts = [];
  if (modes.includes("clinicalPacks")) {
    parts.push(
      "vital signs, CP05A IMP Accountability Log, SM02A Temperature Monitoring Log, CP03A Biological Sample Processing Log, CP07B IMP Compliance Check, or adverse event",
    );
  }
  if (modes.includes("generalForms")) {
    parts.push("general forms (as enabled for your organisation)");
  }
  if (!parts.length) {
    return "Ask your Super Admin which Form Builder packs are enabled.";
  }
  return parts.join("; or ");
}

/** Empty canvas + change/review request — UK English guidance. */
function blankCanvasAlert(allowedModes) {
  return {
    fields: [],
    blocked: true,
    replaceCanvas: false,
    summary: `The canvas is empty. Load a prototype or create a form first. I can build: ${availableFormsBlurb(allowedModes)}.`,
  };
}

function canvasSnapshotLines(currentFields, limit = 12) {
  const fields = Array.isArray(currentFields) ? currentFields : [];
  const lines = fields.slice(0, limit).map((f, i) => {
    const req = f.required ? "*" : "";
    return `${i + 1}. ${fieldDisplayLabel(f)}${req} (${f.type || "text"})`;
  });
  if (fields.length > limit) {
    lines.push(`…and ${fields.length - limit} more.`);
  }
  return lines;
}

function describeCanvasBrief(currentFields, meta = {}) {
  const fields = Array.isArray(currentFields) ? currentFields : [];
  const formName = String(meta.formName || meta.formHeader || "").trim();
  const title = formName ? `“${formName}”` : "Your form";
  const lines = [
    `${title} is open on the canvas (${fields.length} fields).`,
    ...canvasSnapshotLines(fields, 10),
  ];
  return lines.join("\n");
}

/**
 * Merge a short follow-up with the previous user ask
 * e.g. “after the 6th” after “add a divider”.
 */
function resolveFollowUpPrompt(text, recentMessages = []) {
  const t = String(text || "").trim();
  if (!t || !Array.isArray(recentMessages) || !recentMessages.length) return t;

  const priorUsers = recentMessages
    .filter((m) => m?.role === "user" && String(m.text || "").trim())
    .map((m) => String(m.text).trim());
  const lastUser = priorUsers[priorUsers.length - 1] || "";
  if (!lastUser || lastUser === t) return t;

  const isShortFollowUp =
    t.length < 80 &&
    (/^(after|before)\b/i.test(t) ||
      /^(do it|yes|ok|okay|that one|there|same|again)\b/i.test(t) ||
      /^(at|on)\s+(the\s+)?\d+/i.test(t) ||
      /^\d+(st|nd|rd|th)?\s+(field|position)/i.test(t));

  if (!isShortFollowUp) return t;

  const priorIsAction =
    /\b(add|insert|put|place|remove|delete|divider|section|spacer|move|make|change)\b/i.test(
      lastUser,
    );
  if (!priorIsAction) return t;
  return `${lastUser}. ${t}`;
}

function chatAboutCurrentForm(currentFields, meta = {}) {
  const fields = Array.isArray(currentFields) ? currentFields : [];
  const inputs = inputFieldsOnly(fields);
  const formName = String(meta.formName || meta.formHeader || "").trim();
  const label = formName ? `“${formName}”` : "This form";
  const snapshot = canvasSnapshotLines(fields, 8).join("\n");
  return {
    fields: [],
    blocked: false,
    replaceCanvas: false,
    summary: [
      `${label} is on the canvas (${fields.length} fields, ${inputs.length} inputs). I am working on this form.`,
      snapshot,
      "",
      "Try: “add a divider after the 6th field”, “remove Study ID”, “set Quantity in as required”, “Quantity in change type to text with 3 digits only”, or “add Comments and Pharmacist name”.",
    ].join("\n"),
  };
}

function smallTalkReply(hasCanvas, currentFields, meta = {}) {
  if (hasCanvas) {
    return {
      fields: [],
      blocked: false,
      replaceCanvas: false,
      summary: `Sounds good — still on ${String(meta.formName || meta.formHeader || "this form").trim() || "this form"} (${(currentFields || []).length} fields). What should I change next?`,
    };
  }
  return {
    fields: [],
    blocked: false,
    replaceCanvas: false,
    summary:
      "OK. Load or create a form first, then I can help you change it.",
  };
}

function fieldDisplayLabel(field) {
  return String(field?.label || field?.fieldName || field?.type || "Field").trim();
}

function inputFieldsOnly(fields) {
  return (fields || []).filter(
    (f) => f && !isLayoutField(f.type) && f.type !== "calculated",
  );
}

/**
 * Heuristic design review of the current canvas (client-side).
 * Does not replace fields — returns advice only.
 */
function reviewCurrentForm(currentFields, meta = {}) {
  const fields = Array.isArray(currentFields) ? currentFields : [];
  const inputs = inputFieldsOnly(fields);
  const layoutCount = fields.length - inputs.length;
  const requiredCount = inputs.filter((f) => f.required).length;
  const optionalCount = inputs.length - requiredCount;
  const formName = String(meta.formName || meta.formHeader || "").trim();

  const findings = [];
  const suggestions = [];

  findings.push(
    `Canvas has ${fields.length} field(s): ${inputs.length} input field(s)` +
      (layoutCount ? `, ${layoutCount} layout/section field(s)` : "") +
      `.`,
  );
  findings.push(
    `${requiredCount} required, ${optionalCount} optional input field(s).`,
  );

  if (!inputs.length) {
    findings.push("No input fields yet — only layout or empty canvas.");
    suggestions.push(
      "Add clinical fields (for example Study ID, date, and a performed-by field), or ask AQ Assist to generate a clinical pack.",
    );
  }

  const blankLabels = inputs.filter((f) => !String(f.label || "").trim());
  if (blankLabels.length) {
    findings.push(`${blankLabels.length} field(s) have no label.`);
    suggestions.push("Give every input field a clear label before publish.");
  }

  const labelMap = new Map();
  inputs.forEach((f) => {
    const key = fieldDisplayLabel(f).toLowerCase();
    if (!key) return;
    labelMap.set(key, (labelMap.get(key) || 0) + 1);
  });
  const dupes = [...labelMap.entries()]
    .filter(([, n]) => n > 1)
    .map(([k]) => k);
  if (dupes.length) {
    findings.push(
      `Duplicate labels: ${dupes.slice(0, 4).map(titleCase).join(", ")}.`,
    );
    suggestions.push("Rename duplicate labels so staff can tell fields apart.");
  }

  const sections = fields.filter((f) => f.type === "section");
  if (inputs.length >= 8 && sections.length === 0) {
    findings.push("Long form with no section headings.");
    suggestions.push(
      "Add section headers (for example Identification, Product, Sign-off) to group related fields.",
    );
  }

  if (inputs.length && requiredCount === 0) {
    findings.push("No fields are marked required.");
    suggestions.push(
      "Mark critical identifiers and dates as required so blank critical data cannot be saved at fill time.",
    );
  }

  if (inputs.length >= 6 && requiredCount === inputs.length) {
    findings.push("Every input field is required.");
    suggestions.push(
      "Consider making notes/comments optional so capture is not blocked.",
    );
  }

  const choiceGaps = inputs.filter(
    (f) =>
      isChoiceWithOptions(f.type) &&
      (!Array.isArray(f.options) || f.options.length < 2),
  );
  if (choiceGaps.length) {
    findings.push(
      `${choiceGaps.length} choice field(s) need more options: ${choiceGaps
        .slice(0, 3)
        .map(fieldDisplayLabel)
        .join(", ")}.`,
    );
    suggestions.push("Open each choice field and add at least two options.");
  }

  const labelBlob = inputs.map(fieldDisplayLabel).join(" ").toLowerCase();
  const hasId = /\bid\b|subject|participant|volunteer|study/i.test(labelBlob);
  const hasDate = /date|time|datetime/i.test(labelBlob);
  const hasSignOff =
    /performed|checked|received by|dispensed by|completed by|signature|sign.?off/i.test(
      labelBlob,
    );
  const looksPharmacy =
    /imp|batch|lot|kit|dispense|pharmacy|accountability|expiry/i.test(labelBlob);

  if (inputs.length && !hasId) {
    suggestions.push(
      "Consider an identifier field (Study ID, Site ID, or Participant ID).",
    );
  }
  if (inputs.length && !hasDate) {
    suggestions.push("Consider a date or date-time field for when the activity happened.");
  }
  if (inputs.length >= 4 && !hasSignOff) {
    suggestions.push(
      "Add a performed / checked / received-by field for accountability.",
    );
  }
  if (looksPharmacy) {
    if (!/batch|lot/i.test(labelBlob)) {
      suggestions.push("Pharmacy-style forms usually include Batch / lot number.");
    }
    if (!/expir/i.test(labelBlob)) {
      suggestions.push("Consider an Expiry date field for IMP / kit tracking.");
    }
  }

  const preview = inputs
    .slice(0, 8)
    .map((f) => `• ${fieldDisplayLabel(f)}${f.required ? " (required)" : ""}`)
    .join("\n");

  const uniqueSuggestions = [...new Set(suggestions)].slice(0, 8);
  const lines = [
    formName
      ? `Review of “${formName}” on the canvas:`
      : "Review of your current form on the canvas:",
    "",
    ...findings.map((f) => `• ${f}`),
  ];
  if (preview) {
    lines.push("", "Fields (sample):", preview);
    if (inputs.length > 8) {
      lines.push(`• …and ${inputs.length - 8} more.`);
    }
  }
  if (uniqueSuggestions.length) {
    lines.push("", "Suggestions:");
    uniqueSuggestions.forEach((s) => lines.push(`• ${s}`));
  } else {
    lines.push(
      "",
      "No major gaps found from a quick check. You can still ask to add or change a specific field.",
    );
  }
  lines.push(
    "",
    "Tip: say “add a Comments field” or “make Notes optional” to change the canvas.",
  );

  return {
    fields: [],
    blocked: false,
    replaceCanvas: false,
    summary: lines.join("\n"),
  };
}

function normalizeAssistTypos(text) {
  return String(text || "")
    .replace(/\bafteer\b|\bafterr\b|\baftar\b|\baftre\b/gi, "after")
    .replace(/\bbeofre\b|\bbefor\b|\bbeforee\b/gi, "before")
    .replace(/\bbetwen\b|\bbetweenn\b|\bbtw\b/gi, "between")
    .replace(/\bdivert\b|\bdivder\b|\bdevider\b|\bdividr\b/gi, "divider")
    .replace(/\bpositon\b|\bposotion\b|\bposittion\b/gi, "position")
    .replace(/\bpositione?d\b/gi, "positioned")
    .replace(/\bshoudl\b|\bshoud\b|\bshld\b/gi, "should")
    .replace(/\brequied\b|\brequried\b|\bmandatry\b/gi, "required")
    .replace(/\boptionl\b|\boptinal\b/gi, "optional")
    .replace(/\bremve\b|\bdeleete\b|\bdelet\b/gi, "remove")
    .replace(/\brenmae\b|\brelable\b/gi, "rename")
    .replace(/\bmeas\b/gi, "mean")
    .replace(/\bwagirah\b/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/**
 * Insert divider / section / spacer after an Nth field or named field.
 * Understands typos like “divert” / “divder”.
 */
function tryInsertLayoutItem(text, fields, findFields, cleanFieldNeedle) {
  const raw = normalizeAssistTypos(text);
  const wantsDivider =
    /\bdivid(?:er|ers)?\b|\bseparator\b|\bhorizontal\s*line\b/i.test(raw);
  const wantsSection = /\bsection\b|\bheading\b/i.test(raw);
  const wantsSpacer = /\bspacer\b/i.test(raw) && !wantsDivider;
  if (!wantsDivider && !wantsSection && !wantsSpacer) return null;

  const type = wantsDivider ? "divider" : wantsSection ? "section" : "spacer";
  const label =
    type === "section"
      ? "Section"
      : type === "divider"
        ? "Divider"
        : "Spacer";

  const betweenMatch = raw.match(
    /between\s+(?:the\s+)?(?:field\s+)?(?:#)?(\d+)(?:st|nd|rd|th)?(?:\s+field)?\s+and\s+(?:the\s+)?(?:field\s+)?(?:#)?(\d+)/i,
  );
  if (betweenMatch) {
    const a = Number(betweenMatch[1]);
    const b = Number(betweenMatch[2]);
    if (
      Number.isFinite(a) &&
      Number.isFinite(b) &&
      a >= 1 &&
      a <= fields.length &&
      b >= 1 &&
      b <= fields.length &&
      Math.abs(a - b) === 1
    ) {
      const after = Math.min(a, b);
      const next = [...fields];
      next.splice(after, 0, fieldFromSpec({ type, label }, fields));
      return {
        fields: next,
        blocked: false,
        replaceCanvas: true,
        incremental: true,
        summary: `Inserted a ${type} between fields ${Math.min(a, b)} and ${Math.max(a, b)}.`,
      };
    }
    if (Number.isFinite(a) && a >= 1 && a < fields.length) {
      const next = [...fields];
      next.splice(a, 0, fieldFromSpec({ type, label }, fields));
      return {
        fields: next,
        blocked: false,
        replaceCanvas: true,
        incremental: true,
        summary: `Inserted a ${type} after field ${a}.`,
      };
    }
  }

  const afterPos =
    raw.match(
      /after\s+(?:the\s+)?(?:field\s+)?(?:that\s+)?(?:is\s+)?(?:position(?:ed)?\s+)?(?:on\s+)?(?:at\s+)?(?:the\s+)?(?:number\s+)?(?:#)?(\d+)(?:st|nd|rd|th)?(?:\s+(?:position|field|number|place))?/i,
    ) ||
    raw.match(
      /(?:at|on|after)\s+(?:the\s+)?(\d+)(?:st|nd|rd|th)?\s+(?:position|field|number|place)/i,
    ) ||
    raw.match(/(\d+)(?:st|nd|rd|th)?\s+(?:position|field|number|place)/i) ||
    raw.match(/after\s+(?:field\s+)?(?:#)?(\d+)\b/i);

  if (afterPos) {
    const n = Number(afterPos[1]);
    if (!Number.isFinite(n) || n < 1) {
      return {
        fields: [],
        blocked: false,
        replaceCanvas: false,
        summary:
          "Tell me a valid field position, for example “after the 6th field”.",
      };
    }
    if (n > fields.length) {
      return {
        fields: [],
        blocked: false,
        replaceCanvas: false,
        summary: `There is no field at position ${n}. This form has ${fields.length} fields.`,
      };
    }
    const insertAt = n;
    const next = [...fields];
    next.splice(insertAt, 0, fieldFromSpec({ type, label }, fields));
    const anchor = fieldDisplayLabel(fields[n - 1]);
    return {
      fields: next,
      blocked: false,
      replaceCanvas: true,
      incremental: true,
      summary: `Inserted a ${type} after field ${n} (“${anchor}”).`,
    };
  }

  const beforePos = raw.match(
    /before\s+(?:the\s+)?(?:field\s+)?(?:at\s+)?(?:the\s+)?(?:#)?(\d+)(?:st|nd|rd|th)?(?:\s+(?:position|field))?/i,
  );
  if (beforePos) {
    const n = Number(beforePos[1]);
    if (!Number.isFinite(n) || n < 1 || n > fields.length) {
      return {
        fields: [],
        blocked: false,
        replaceCanvas: false,
        summary: `There is no field at position ${beforePos[1]}. This form has ${fields.length} fields.`,
      };
    }
    const next = [...fields];
    next.splice(n - 1, 0, fieldFromSpec({ type, label }, fields));
    return {
      fields: next,
      blocked: false,
      replaceCanvas: true,
      incremental: true,
      summary: `Inserted a ${type} before field ${n} (“${fieldDisplayLabel(fields[n - 1])}”).`,
    };
  }

  const afterName = raw.match(
    /after\s+(?:the\s+)?(?:field\s+)?["']?([^"'\n]+?)["']?\s*$/i,
  );
  if (afterName) {
    let needle = cleanFieldNeedle(afterName[1]);
    needle = needle
      .replace(
        /\bdivid(?:er|ers)?\b|\bsection\b|\bspacer\b|\bseparator\b/gi,
        "",
      )
      .replace(/^(?:a|an|the|add|insert|put|place|want|i mean.*?)\s+/i, "")
      .trim();
    if (needle && !/^(this|form|canvas|here|please)$/i.test(needle)) {
      const matches = findFields(needle);
      if (matches.length) {
        const idx = fields.findIndex((f) => f.id === matches[0].id);
        const next = [...fields];
        next.splice(idx + 1, 0, fieldFromSpec({ type, label }, fields));
        return {
          fields: next,
          blocked: false,
          replaceCanvas: true,
          incremental: true,
          summary: `Inserted a ${type} after “${fieldDisplayLabel(matches[0])}”.`,
        };
      }
    }
  }

  if (
    /\b(add|insert|put|place|want|need|should)\b/i.test(raw) ||
    wantsDivider ||
    wantsSection ||
    wantsSpacer
  ) {
    const next = [...fields, fieldFromSpec({ type, label }, fields)];
    return {
      fields: next,
      blocked: false,
      replaceCanvas: true,
      incremental: true,
      summary: `Added a ${type} at the end of the form. Say “after the 6th field” if you want a specific place.`,
    };
  }

  return null;
}

/**
 * Incremental edit of current canvas fields from natural language.
 * Returns null if the prompt is not a clear edit instruction.
 */
function tryEditCurrentForm(prompt, currentFields) {
  const text = normalizeAssistTypos(String(prompt || "").trim());
  const fields = Array.isArray(currentFields)
    ? currentFields.map((f) => ({ ...f }))
    : [];
  if (!fields.length) return null;

  const findFields = (needle) => {
    const target = normalizeFieldKey(needle);
    if (!target) return [];
    return fields.filter((f) => {
      const labelKey = normalizeFieldKey(fieldDisplayLabel(f));
      const nameKey = normalizeFieldKey(f.fieldName || f.name || "");
      return (
        labelKey === target ||
        nameKey === target ||
        labelKey.includes(target) ||
        target.includes(labelKey) ||
        nameKey.includes(target)
      );
    });
  };

  const cleanFieldNeedle = (raw) =>
    String(raw || "")
      .replace(/\b(field|filed|feild|fields)\b/gi, "")
      .replace(/\bfrom\s+(this|the|my)\s+form\b/gi, "")
      .replace(/\bto\s+(this|the|my)\s+form\b/gi, "")
      .replace(/\bon\s+(this|the|my)\s+(form|canvas)\b/gi, "")
      .replace(/\bthanks?\b/gi, "")
      .replace(/\bafter\b.*$/i, "")
      .replace(/^(?:a|an|the|new|called|named|for)\s+/i, "")
      .replace(/\s+(?:please|thanks?)$/i, "")
      .trim();

  const layoutInsert = tryInsertLayoutItem(text, fields, findFields, cleanFieldNeedle);
  if (layoutInsert) return layoutInsert;

  const allRequired = text.match(
    /(?:please\s+|can you\s+)?make\s+(?:all|every)\s+(?:fields?\s+)?(required|mandatory|optional|not required)/i,
  );
  if (allRequired) {
    const makeRequired =
      /required|mandatory/i.test(allRequired[1]) &&
      !/not required|optional/i.test(allRequired[1]);
    let changed = 0;
    const next = fields.map((f) => {
      if (isLayoutField(f.type)) return f;
      changed += 1;
      return { ...f, required: makeRequired };
    });
    if (changed) {
      return {
        fields: next,
        blocked: false,
        replaceCanvas: true,
        incremental: true,
        summary: `Marked ${changed} input field(s) as ${
          makeRequired ? "required" : "optional"
        }.`,
      };
    }
  }

  const swapMatch = text.match(
    /(?:please\s+|can you\s+)?swap\s+(?:fields?\s+)?(?:#)?(\d+)(?:st|nd|rd|th)?(?:\s+and\s+|\s*&\s*|\s+with\s+)(?:fields?\s+)?(?:#)?(\d+)/i,
  );
  if (swapMatch) {
    const a = Number(swapMatch[1]);
    const b = Number(swapMatch[2]);
    if (
      a >= 1 &&
      b >= 1 &&
      a <= fields.length &&
      b <= fields.length &&
      a !== b
    ) {
      const next = [...fields];
      const tmp = next[a - 1];
      next[a - 1] = next[b - 1];
      next[b - 1] = tmp;
      return {
        fields: next,
        blocked: false,
        replaceCanvas: true,
        incremental: true,
        summary: `Swapped fields ${a} and ${b}.`,
      };
    }
  }

  const dupMatch = text.match(
    /(?:please\s+|can you\s+)?(?:duplicate|copy)\s+(?:the\s+)?(?:field\s+)?(?:at\s+)?(?:position\s+|#)?(\d+)(?:st|nd|rd|th)?(?:\s+field)?/i,
  );
  if (dupMatch) {
    const n = Number(dupMatch[1]);
    if (n >= 1 && n <= fields.length) {
      const src = fields[n - 1];
      const copy = fieldFromSpec(
        {
          type: src.type,
          label: `${fieldDisplayLabel(src)} (copy)`,
          required: !!src.required,
          width: src.width,
          options: src.options,
        },
        fields,
      );
      const next = [...fields];
      next.splice(n, 0, copy);
      return {
        fields: next,
        blocked: false,
        replaceCanvas: true,
        incremental: true,
        summary: `Duplicated field ${n} (“${fieldDisplayLabel(src)}”).`,
      };
    }
  }

  // Remove / update by position: “remove the 6th field”, “make field 3 required”
  const posRemove = text.match(
    /(?:please\s+|can you\s+)?(?:remove|delete)\s+(?:the\s+|a\s+|an\s+)?(?:field\s+)?(?:at\s+)?(?:position\s+|#)?(\d+)(?:st|nd|rd|th)?(?:\s+field)?(?:\s+from\b.*)?$/i,
  );
  if (posRemove) {
    const n = Number(posRemove[1]);
    if (n >= 1 && n <= fields.length) {
      const removed = fields[n - 1];
      const next = fields.filter((_, i) => i !== n - 1);
      return {
        fields: next,
        blocked: false,
        replaceCanvas: true,
        incremental: true,
        summary: `Removed field ${n} (“${fieldDisplayLabel(removed)}”).`,
      };
    }
    return {
      fields: [],
      blocked: false,
      replaceCanvas: false,
      summary: `There is no field at position ${posRemove[1]}. This form has ${fields.length} fields.`,
    };
  }

  const posRequired = text.match(
    /(?:please\s+|can you\s+)?make\s+(?:the\s+|a\s+|an\s+)?(?:field\s+)?(?:at\s+)?(?:position\s+|#)?(\d+)(?:st|nd|rd|th)?(?:\s+field)?\s+(required|mandatory|optional|not required)/i,
  );
  if (posRequired) {
    const n = Number(posRequired[1]);
    const makeRequired =
      /required|mandatory/i.test(posRequired[2]) &&
      !/not required|optional/i.test(posRequired[2]);
    if (n >= 1 && n <= fields.length) {
      const target = fields[n - 1];
      if (isLayoutField(target.type)) {
        return {
          fields: [],
          blocked: false,
          replaceCanvas: false,
          summary: `Field ${n} is a layout item (“${fieldDisplayLabel(target)}”) and cannot be required.`,
        };
      }
      const next = fields.map((f, i) =>
        i === n - 1 ? { ...f, required: makeRequired } : f,
      );
      return {
        fields: next,
        blocked: false,
        replaceCanvas: true,
        incremental: true,
        summary: `Marked field ${n} (“${fieldDisplayLabel(target)}”) as ${
          makeRequired ? "required" : "optional"
        }.`,
      };
    }
    return {
      fields: [],
      blocked: false,
      replaceCanvas: false,
      summary: `There is no field at position ${posRequired[1]}. This form has ${fields.length} fields.`,
    };
  }

  const multiAdd = tryAddMultipleFields(text, fields);
  if (multiAdd) return multiAdd;

  const moveMatch = text.match(
    /(?:please\s+|can you\s+)?(?:move|put|place)\s+(?:the\s+|a\s+|an\s+)?["']?([^"'\n]+?)["']?\s+(?:to|at)\s+(?:position\s+|field\s+|#)?(\d+)(?:st|nd|rd|th)?\b/i,
  );
  if (moveMatch) {
    const needle = cleanFieldNeedle(moveMatch[1]);
    const pos = Number(moveMatch[2]);
    const matches = findFields(needle);
    if (!matches.length) {
      return {
        fields: [],
        blocked: false,
        replaceCanvas: false,
        summary: `I could not find a field matching “${needle}” to move.`,
      };
    }
    if (!Number.isFinite(pos) || pos < 1 || pos > fields.length) {
      return {
        fields: [],
        blocked: false,
        replaceCanvas: false,
        summary: `Position ${moveMatch[2]} is out of range. This form has ${fields.length} fields.`,
      };
    }
    const moving = matches[0];
    const without = fields.filter((f) => f.id !== moving.id);
    const insertAt = Math.min(Math.max(pos - 1, 0), without.length);
    without.splice(insertAt, 0, moving);
    return {
      fields: without,
      blocked: false,
      replaceCanvas: true,
      incremental: true,
      summary: `Moved “${fieldDisplayLabel(moving)}” to position ${pos}.`,
    };
  }

  const addMatch =
    text.match(
      /(?:please\s+|can you\s+|could you\s+)?add\s+(?:a\s+|an\s+|the\s+)?(?:new\s+)?field\s+(?:called\s+|named\s+|for\s+)?["']?([^"'\n]+?)["']?(?:\s+(?:to|from|on)\b.*)?$/i,
    ) ||
    text.match(
      /(?:please\s+|can you\s+|could you\s+)?add\s+(?:a\s+|an\s+|the\s+)?(?:new\s+)?["']?([^"'\n]+?)["']?\s+field(?:\s+(?:to|from|on)\b.*)?$/i,
    ) ||
    text.match(
      /(?:please\s+|can you\s+|could you\s+)?add\s+(?:a\s+|an\s+|the\s+)?(?:new\s+)?["']?([^"'\n]+?)["']?(?:\s+field)?(?:\s+(?:to|from|on)\b.*)?$/i,
    ) ||
    text.match(
      /(?:please\s+|can you\s+|could you\s+)?(?:insert|include)\s+(?:a\s+|an\s+|the\s+)?["']?([^"'\n]+?)["']?(?:\s+field)?(?:\s+(?:to|from|on)\b.*)?$/i,
    );

  if (
    addMatch &&
    !/\breview\b|\bgaps?\b/i.test(text) &&
    !/\bdivid|\bdivert|\bdivder|\bsection\b|\bspacer\b|\bseparator\b/i.test(text)
  ) {
    let label = cleanFieldNeedle(addMatch[1]);
    if (/^e-?mails?$/i.test(label) || /^email\s*id$/i.test(label) || /^emailid$/i.test(label)) {
      label = /id/i.test(label) ? "Email ID" : "Email";
    } else if (/^phones?$/i.test(label) || /^mobile$/i.test(label)) {
      label = /^mobile$/i.test(label) ? "Mobile" : "Phone";
    }
    if (
      label &&
      label.length < 80 &&
      !/^(this|my|the|current|form|canvas|here|please|new)$/i.test(label)
    ) {
      const type = inferFieldType(label);
      const next = [
        ...fields,
        fieldFromSpec({ type, label, required: false }, fields),
      ];
      return {
        fields: next,
        blocked: false,
        replaceCanvas: true,
        incremental: true,
        summary: `Added “${label}” (${type}) to your form. Check it on the canvas.`,
      };
    }
  }

  const removeMatch = text.match(
    /(?:please\s+|can you\s+|could you\s+)?(?:remove|delete)\s+(?:the\s+|a\s+|an\s+)?(?:field\s+)?(?:called\s+|named\s+)?["']?([^"'\n]+?)["']?(?:\s+field)?(?:\s+from\b.*)?$/i,
  );
  if (removeMatch) {
    const needle = cleanFieldNeedle(removeMatch[1]);
    const matches = findFields(needle);
    if (matches.length) {
      const ids = new Set(matches.map((f) => f.id));
      const removedLabels = matches.map((f) => fieldDisplayLabel(f));
      const next = fields.filter((f) => !ids.has(f.id));
      return {
        fields: next,
        blocked: false,
        replaceCanvas: true,
        incremental: true,
        summary:
          matches.length === 1
            ? `Removed “${removedLabels[0]}” from the canvas.`
            : `Removed ${matches.length} fields: ${removedLabels
                .map((l) => `“${l}”`)
                .join(", ")}.`,
      };
    }
    return {
      fields: [],
      blocked: false,
      replaceCanvas: false,
      summary: `I could not find a field matching “${needle}” on the canvas. Check the label and try again.`,
    };
  }

  // “make Notes required”, “set Quantity in and Quantity out fields as required”
  const reqMatch = text.match(
    /(?:please\s+|can you\s+|could you\s+)?(?:make|set|mark)\s+(?:the\s+|these\s+)?["']?(.+?)["']?\s+(?:fields?\s+)?(?:as\s+)?(required|mandatory|optional|not required)\b/i,
  );
  if (reqMatch) {
    const rawNeedles = String(reqMatch[1] || "")
      .replace(/\bfields?\b/gi, "")
      .replace(/\bfrom\s+(this|the|my)\s+form\b/gi, "")
      .trim();
    const makeRequired =
      /required|mandatory/i.test(reqMatch[2]) &&
      !/not required|optional/i.test(reqMatch[2]);
    // Skip bare position tokens already handled above (e.g. "field 3")
    if (!/^(?:field\s+)?(?:#)?\d+(?:st|nd|rd|th)?$/i.test(rawNeedles)) {
      const needles = rawNeedles
        .split(/\s*(?:,|\/|&|\band\b)\s*/i)
        .map((s) => cleanFieldNeedle(s))
        .filter(
          (s) =>
            s.length > 1 &&
            !/^(?:all|every|these|those|the|a|an|field|fields)$/i.test(s),
        );
      if (needles.length) {
        let changed = 0;
        const matchedLabels = [];
        const next = fields.map((f) => {
          if (isLayoutField(f.type)) return f;
          const label = fieldDisplayLabel(f);
          const labelKey = normalizeFieldKey(label);
          const nameKey = normalizeFieldKey(f.fieldName || f.name || "");
          const hit = needles.some((n) => {
            const target = normalizeFieldKey(n);
            if (!target) return false;
            return (
              labelKey === target ||
              nameKey === target ||
              labelKey.includes(target) ||
              target.includes(labelKey) ||
              nameKey.includes(target)
            );
          });
          if (!hit) return f;
          changed += 1;
          matchedLabels.push(label);
          return { ...f, required: makeRequired };
        });
        if (changed) {
          return {
            fields: next,
            blocked: false,
            replaceCanvas: true,
            incremental: true,
            summary: `Marked ${changed} field(s) (${matchedLabels
              .slice(0, 6)
              .join(", ")}${matchedLabels.length > 6 ? "…" : ""}) as ${
              makeRequired ? "required" : "optional"
            }.`,
          };
        }
        return {
          fields: [],
          blocked: false,
          replaceCanvas: false,
          summary: `I could not find field(s) matching “${needles.join(
            ", ",
          )}” to update. Check the label and try again.`,
        };
      }
    }
  }

  const renameMatch = text.match(
    /(?:please\s+|can you\s+)?(?:rename|relabel|change\s+label\s+(?:of|for)|call)\s+["']?([^"'\n]+?)["']?\s+(?:to|as)\s+["']?([^"'\n]+?)["']?\s*$/i,
  );
  if (renameMatch) {
    const from = renameMatch[1].trim();
    const to = renameMatch[2].trim();
    let changed = 0;
    const next = fields.map((f) => {
      if (!fieldDisplayLabel(f).toLowerCase().includes(from.toLowerCase())) {
        return f;
      }
      changed += 1;
      return {
        ...f,
        label: to,
        fieldName: ensureUniqueFieldName(
          slugifyFieldName(to),
          fields.filter((x) => x.id !== f.id),
          f.id,
        ),
      };
    });
    if (changed) {
      return {
        fields: next,
        blocked: false,
        replaceCanvas: true,
        incremental: true,
        summary: `Renamed ${changed} field(s) matching “${from}” to “${to}”.`,
      };
    }
    return {
      fields: [],
      blocked: false,
      replaceCanvas: false,
      summary: `I could not find a field matching “${from}” to rename.`,
    };
  }

  const typeChange = tryChangeFieldTypeAndValidation(text, fields, findFields);
  if (typeChange) return typeChange;

  return null;
}

/** Parse “3 digits”, “max length 10”, “numbers only”, etc. */
function parseValidationHints(text) {
  const raw = String(text || "");
  const patch = {};
  const exactDigits = raw.match(
    /(?:exactly\s+|only\s+)?(\d+)\s*[- ]?\s*digits?(?:\s+only)?/i,
  );
  const upToDigits = raw.match(
    /(?:up\s*to|maximum\s+of|max(?:imum)?)\s+(\d+)\s*digits?/i,
  );
  const maxLen = raw.match(
    /(?:max(?:imum)?\s*length|maxlength|max\s*char(?:acter)?s?)\s*(?:of\s*|is\s*|=|:)?\s*(\d+)/i,
  );
  const minLen = raw.match(
    /(?:min(?:imum)?\s*length|minlength|min\s*char(?:acter)?s?)\s*(?:of\s*|is\s*|=|:)?\s*(\d+)/i,
  );
  const numbersOnly =
    /\b(?:accept\s+)?(?:only\s+)?(?:numbers?|digits?|numeric)\b/i.test(raw) ||
    /\bnumbers?\s+only\b/i.test(raw) ||
    /\bdigits?\s+only\b/i.test(raw);

  if (exactDigits) {
    const n = Number(exactDigits[1]);
    if (Number.isFinite(n) && n > 0 && n <= 50) {
      patch.minLength = n;
      patch.maxLength = n;
      patch.pattern = `^\\d{${n}}$`;
    }
  } else if (upToDigits) {
    const n = Number(upToDigits[1]);
    if (Number.isFinite(n) && n > 0 && n <= 50) {
      patch.maxLength = n;
      patch.pattern = `^\\d{1,${n}}$`;
    }
  } else if (maxLen || minLen) {
    if (maxLen) patch.maxLength = Number(maxLen[1]);
    if (minLen) patch.minLength = Number(minLen[1]);
  }

  if (numbersOnly && !patch.pattern) {
    if (patch.maxLength && patch.minLength && patch.maxLength === patch.minLength) {
      patch.pattern = `^\\d{${patch.maxLength}}$`;
    } else if (patch.maxLength) {
      patch.pattern = `^\\d{1,${patch.maxLength}}$`;
    } else {
      patch.pattern = "^\\d+$";
    }
  }

  const minVal = raw.match(
    /(?:min(?:imum)?\s*(?:value|number)?|at\s+least)\s*(?:of\s*|is\s*|=|:)?\s*(-?\d+(?:\.\d+)?)/i,
  );
  const maxVal = raw.match(
    /(?:max(?:imum)?\s*(?:value|number)?|at\s+most|no\s+more\s+than)\s*(?:of\s*|is\s*|=|:)?\s*(-?\d+(?:\.\d+)?)/i,
  );
  if (minVal) patch.minValue = Number(minVal[1]);
  if (maxVal) patch.maxValue = Number(maxVal[1]);

  return patch;
}

function resolveFieldTypeToken(typeTokenRaw) {
  let typeToken = String(typeTokenRaw || "")
    .toLowerCase()
    .replace(/\s+/g, "");
  if (typeToken === "textarea" || typeToken === "textare") typeToken = "textarea";
  if (typeToken === "yes/no" || typeToken === "yesno") typeToken = "yesno";
  if (typeToken === "datetime" || typeToken === "dateandtime") {
    typeToken = "datetime";
  }
  const typeMap = {
    text: "text",
    textarea: "textarea",
    date: "date",
    time: "time",
    datetime: "datetime",
    number: "number",
    integer: "integer",
    dropdown: "dropdown",
    radio: "radio",
    checkbox: "checkbox",
    yesno: "yesno",
    section: "section",
  };
  return typeMap[typeToken] || null;
}

/**
 * Natural-language type + validation edits, e.g.
 * “Quantity in change the field type to text but accept only number with 3 digit only”
 */
function tryChangeFieldTypeAndValidation(text, fields, findFields) {
  const raw = String(text || "").trim();
  if (!raw) return null;

  const typeWord =
    "text\\s*area|textarea|text|date\\s*time|datetime|date|time|number|integer|dropdown|radio|checkbox|yes\\s*\\/?\\s*no|yesno|section";

  let needle = "";
  let pos = null;
  let newType = null;
  let restForValidation = raw;

  // “Quantity in change the field type to text …”
  let m = raw.match(
    new RegExp(
      `^["']?(.+?)["']?\\s+(?:please\\s+)?(?:change|convert|update|set)\\s+(?:the\\s+)?(?:field\\s+)?type\\s+to\\s+(a\\s+|an\\s+)?(${typeWord})\\b([\\s\\S]*)$`,
      "i",
    ),
  );
  if (m) {
    needle = cleanFieldLabelNeedle(m[1]);
    newType = resolveFieldTypeToken(m[3] || m[2]);
    restForValidation = `${m[1]} ${m[4] || ""}`;
  }

  // “change the field type of Quantity in to text”
  if (!newType) {
    m = raw.match(
      new RegExp(
        `(?:change|convert|update|set)\\s+(?:the\\s+)?(?:field\\s+)?type\\s+(?:of|for)\\s+["']?(.+?)["']?\\s+to\\s+(a\\s+|an\\s+)?(${typeWord})\\b([\\s\\S]*)$`,
        "i",
      ),
    );
    if (m) {
      needle = cleanFieldLabelNeedle(m[1]);
      newType = resolveFieldTypeToken(m[3] || m[2]);
      restForValidation = m[4] || "";
    }
  }

  // “change Quantity in to text …” / “convert field 7 to integer”
  if (!newType) {
    m = raw.match(
      new RegExp(
        `(?:please\\s+|can you\\s+)?(?:change|convert|update|set)\\s+(?:the\\s+|a\\s+|an\\s+)?(?:field\\s+)?(?:at\\s+)?(?:position\\s+|#)?(\\d+)(?:st|nd|rd|th)?(?:\\s+field)?\\s+(?:type\\s+)?(?:to|into)\\s+(a\\s+|an\\s+)?(${typeWord})\\b([\\s\\S]*)$`,
        "i",
      ),
    );
    if (m) {
      pos = Number(m[1]);
      newType = resolveFieldTypeToken(m[3] || m[2]);
      restForValidation = m[4] || "";
    }
  }

  if (!newType) {
    m = raw.match(
      new RegExp(
        `(?:please\\s+|can you\\s+)?(?:change|convert|update|set)\\s+(?:the\\s+|a\\s+|an\\s+)?["']?([^"'\\n]+?)["']?\\s+(?:field\\s+)?(?:type\\s+)?(?:to|into)\\s+(a\\s+|an\\s+)?(${typeWord})\\b([\\s\\S]*)$`,
        "i",
      ),
    );
    if (m) {
      const maybe = cleanFieldLabelNeedle(m[1]);
      // Reject junk needles like “the field type”
      if (
        maybe &&
        !/^(?:the\s+)?(?:field\s+)?type$/i.test(maybe) &&
        !/^(?:to|into|a|an)$/i.test(maybe)
      ) {
        needle = maybe;
        newType = resolveFieldTypeToken(m[3] || m[2]);
        restForValidation = m[4] || "";
      }
    }
  }

  // “make Quantity in a text field”
  if (!newType) {
    m = raw.match(
      new RegExp(
        `(?:please\\s+|can you\\s+)?make\\s+["']?([^"'\\n]+?)["']?\\s+(?:a\\s+|an\\s+)?(${typeWord})(?:\\s+field)?\\b([\\s\\S]*)$`,
        "i",
      ),
    );
    if (m) {
      needle = cleanFieldLabelNeedle(m[1]);
      newType = resolveFieldTypeToken(m[2]);
      restForValidation = m[3] || "";
    }
  }

  // Validation-only: “Quantity in accept only 3 digits” / “set max length of Comments to 200”
  const validationOnly =
    !newType &&
    /\b(digit|max\s*length|min\s*length|pattern|numbers?\s+only|accept\s+only)\b/i.test(
      raw,
    );
  const validationPatch = parseValidationHints(
    validationOnly ? raw : restForValidation,
  );

  if (!newType && !Object.keys(validationPatch).length) return null;

  if (validationOnly && !needle) {
    m = raw.match(
      /^["']?(.+?)["']?\s+(?:should\s+|must\s+|please\s+)?(?:accept|allow|use|have|be|set)/i,
    );
    if (m) needle = cleanFieldLabelNeedle(m[1]);
    if (!needle) {
      m = raw.match(
        /(?:for|on|of)\s+["']?([^"'\n]+?)["']?(?:\s+field)?(?:\s|$)/i,
      );
      if (m) needle = cleanFieldLabelNeedle(m[1]);
    }
  }

  let targets = [];
  if (Number.isFinite(pos) && pos >= 1 && pos <= fields.length) {
    targets = [fields[pos - 1]];
  } else if (needle) {
    targets = findFields(needle);
    // Soft fallback: label starts with / includes needle words
    if (!targets.length) {
      const key = normalizeFieldKey(needle);
      targets = fields.filter((f) => {
        const lk = normalizeFieldKey(fieldDisplayLabel(f));
        return lk === key || lk.startsWith(key) || lk.includes(key);
      });
    }
  }

  if (!targets.length) {
    return {
      fields: [],
      blocked: false,
      replaceCanvas: false,
      summary: needle
        ? `I could not find a field matching “${needle}”. Try the exact canvas label (for example “Quantity in”) or “field 7”.`
        : "Tell me which field to change, for example “Quantity in change type to text” or “change field 7 to integer”.",
    };
  }

  const targetIds = new Set(targets.map((f) => f.id));
  let changed = 0;
  const notes = [];
  const next = fields.map((f) => {
    if (!targetIds.has(f.id)) return f;
    if (isLayoutField(f.type) && newType && isLayoutField(newType) === false) {
      // allow converting layout? skip
      return f;
    }
    changed += 1;
    let updated = { ...f };
    if (newType && f.type !== newType) {
      const rebuilt = fieldFromSpec(
        {
          type: newType,
          label: f.label,
          required: !!f.required,
          width: f.width,
          maxLength: f.maxLength,
          minLength: f.minLength,
          pattern: f.pattern,
          minValue: f.minValue,
          maxValue: f.maxValue,
        },
        fields.filter((x) => x.id !== f.id),
      );
      updated = { ...rebuilt, id: f.id, fieldName: f.fieldName };
      notes.push(`${fieldDisplayLabel(f)} → ${newType}`);
    }
    if (Object.keys(validationPatch).length) {
      updated = { ...updated, ...validationPatch };
      const bits = [];
      if (validationPatch.maxLength != null) {
        bits.push(`max length ${validationPatch.maxLength}`);
      }
      if (validationPatch.minLength != null) {
        bits.push(`min length ${validationPatch.minLength}`);
      }
      if (validationPatch.pattern) bits.push("digit pattern");
      if (validationPatch.minValue != null) {
        bits.push(`min ${validationPatch.minValue}`);
      }
      if (validationPatch.maxValue != null) {
        bits.push(`max ${validationPatch.maxValue}`);
      }
      if (bits.length) notes.push(`${fieldDisplayLabel(f)}: ${bits.join(", ")}`);
    }
    return updated;
  });

  if (!changed) {
    return {
      fields: [],
      blocked: false,
      replaceCanvas: false,
      summary: `No changes applied for “${needle || `field ${pos}`}”.`,
    };
  }

  return {
    fields: next,
    blocked: false,
    replaceCanvas: true,
    incremental: true,
    summary: `Updated ${changed} field(s). ${[...new Set(notes)].join("; ")}.`,
  };
}

function cleanFieldLabelNeedle(raw) {
  return String(raw || "")
    .replace(/\bfield\b/gi, "")
    .replace(/\bfrom\s+(this|the|my)\s+form\b/gi, "")
    .replace(/\bto\s+(this|the|my)\s+form\b/gi, "")
    .replace(/\bon\s+(this|the|my)\s+(form|canvas)\b/gi, "")
    .replace(/^(?:the|a|an|please|can you)\s+/i, "")
    .replace(/\s+(?:please|can you|thanks?)$/i, "")
    .trim();
}

/** “add Comments, Pharmacist name and Site phone” onto the current form. */
function tryAddMultipleFields(prompt, fields) {
  const text = String(prompt || "").trim();
  if (!/\b(add|include|insert)\b/i.test(text)) return null;
  if (/\bdivid|\bsection\b|\bspacer\b|\breview\b|\bgaps?\b/i.test(text)) {
    return null;
  }

  let chunk = "";
  const m1 = text.match(
    /\b(?:add|include|insert)\s+(?:these\s+)?(?:fields?\s+)?(?:called\s+|named\s+)?(.+)$/i,
  );
  if (m1) chunk = m1[1];
  if (!chunk) return null;

  chunk = chunk
    .replace(/\bfrom\s+(this|the|my)\s+form\b/gi, "")
    .replace(/\bto\s+(this|the|my)\s+form\b/gi, "")
    .replace(/\bplease\b/gi, "")
    .trim();

  const parts = chunk
    .split(/,|\/|&|\band\b|;/i)
    .map((s) =>
      s
        .replace(/^(?:a|an|the|new|field)\s+/i, "")
        .replace(/\s+field$/i, "")
        .trim(),
    )
    .filter(
      (s) =>
        s.length > 1 &&
        s.length < 60 &&
        !/^(this|my|form|canvas|here|please|fields?)$/i.test(s),
    );

  if (parts.length < 2) return null;

  let next = [...fields];
  const added = [];
  for (const label of parts.slice(0, 12)) {
    const type = inferFieldType(label);
    const field = fieldFromSpec({ type, label, required: false }, next);
    next = [...next, field];
    added.push(label);
  }
  if (!added.length) return null;
  return {
    fields: next,
    blocked: false,
    replaceCanvas: true,
    incremental: true,
    summary: `Added ${added.length} field(s) to your form: ${added.join(", ")}.`,
  };
}

function handleCanvasAssist(prompt, options = {}) {
  const currentFields = options.currentFields;
  const meta = {
    formName: options.formName,
    formHeader: options.formHeader,
  };
  if (!hasCanvasFields(currentFields)) {
    return blankCanvasAlert(options.allowedModes);
  }

  const text = resolveFollowUpPrompt(
    normalizeAssistTypos(prompt),
    options.recentMessages,
  );

  if (isSmallTalk(text)) {
    return smallTalkReply(true, currentFields, meta);
  }

  if (
    /\b(what|which|list|show)\b.+\bfields?\b/i.test(text) ||
    /\bhow many fields\b/i.test(text) ||
    /\bwhat('?s| is) on (the|this|my) (form|canvas)\b/i.test(text)
  ) {
    return {
      fields: [],
      blocked: false,
      replaceCanvas: false,
      summary: describeCanvasBrief(currentFields, meta),
    };
  }

  const edited = tryEditCurrentForm(text, currentFields);
  if (edited) return edited;

  if (isCanvasAssistIntent(text) || isChangeOrReviewRequest(text)) {
    if (/\bdivid|\bdivert|\bdivder|\bseparator\b|\bsection\b|\bspacer\b/i.test(text)) {
      return {
        fields: [],
        blocked: false,
        replaceCanvas: false,
        summary: [
          describeCanvasBrief(currentFields, meta),
          "",
          "Tell me the place, for example: “add a divider after the 6th field” or “insert a section after Study ID”.",
        ].join("\n"),
      };
    }
    if (/\breview\b|\bgaps?\b|improve|suggest|missing|analy/i.test(text)) {
      return reviewCurrentForm(currentFields, meta);
    }
  }

  // Field list on an existing form: “add: comments, site, phone”
  const inlineAdd = parseInlineFields(text);
  if (inlineAdd.length >= 1 && /\b(add|include|insert|with)\b/i.test(text)) {
    let next = [...(currentFields || [])];
    const added = [];
    for (const item of inlineAdd.slice(0, 12)) {
      const field = fieldFromSpec(
        {
          type: item.type,
          label: item.label,
          required: !!item.required,
          width: item.width,
        },
        next,
      );
      next = [...next, field];
      added.push(fieldDisplayLabel(field));
    }
    if (added.length) {
      return {
        fields: next,
        blocked: false,
        replaceCanvas: true,
        incremental: true,
        summary: `Added ${added.length} field(s): ${added.join(", ")}.`,
      };
    }
  }

  if (/\b(add|remove|delete|rename|move|swap|duplicate|change|make)\b/i.test(text)) {
    return {
      fields: [],
      blocked: false,
      replaceCanvas: false,
      summary: [
        describeCanvasBrief(currentFields, meta),
        "",
        "I could not apply that edit exactly. Try a clearer instruction, for example:",
        "• “remove field 4” or “remove Study ID”",
        "• “add Comments and Pharmacist name”",
        "• “make field 3 optional” or “set Quantity in and Quantity out as required”",
        "• “Quantity in change the field type to text with 3 digits only”",
        "• “add a divider after the 6th field”",
      ].join("\n"),
    };
  }

  // Default: stay on this form — never pretend we do not know it.
  return chatAboutCurrentForm(currentFields, meta);
}

/**
 * Build a usable generic form from free text when no pack/intent matched.
 * Uses the prompt as the form name and invents sensible everyday fields.
 */
function buildGenericForm(text) {
  const name = titleCase(clip(text, 70));
  const wantsCustomer = /customer|client|patient|person|user|buyer/i.test(text);
  const wantsDate = /date|when|schedule|appointment/i.test(text);
  const wantsPhone = /phone|mobile|contact|call/i.test(text);
  const wantsEmail = /email|mail/i.test(text);

  const fields = [
    { type: "section", label: "Details" },
    {
      type: "date",
      label: wantsDate ? "Date" : "Form date",
      required: true,
      width: "half",
      defaultCurrentDate: true,
    },
  ];

  if (wantsCustomer || /form|for\b/i.test(text)) {
    fields.push({
      type: "text",
      label: wantsCustomer ? "Customer / person name" : "Name",
      required: true,
      width: "half",
    });
  } else {
    fields.push({
      type: "text",
      label: "Reference / ID",
      required: false,
      width: "half",
    });
  }

  if (wantsPhone) {
    fields.push({
      type: "text",
      label: "Phone",
      required: true,
      width: "half",
    });
  }
  if (wantsEmail) {
    fields.push({
      type: "text",
      label: "Email",
      required: false,
      width: "half",
    });
  }

  fields.push(
    { type: "textarea", label: "Description / details", required: true },
    { type: "textarea", label: "Notes", required: false },
  );

  return {
    fields: mapSpecsWithUniqueNames(fields),
    code: codeFromTitle(name),
    name,
    moduleScope: "General",
    summary: `Generated a general form from your description (${fields.length} fields). Edit labels and types as needed — this is not limited to clinical packs.`,
  };
}

/**
 * Build canvas fields from an uploaded PDF/Word text dump (local path).
 * Complex tables / Yes-No / checklists / sign-off handled in esourceAssistDocumentFormBuild.
 */
function buildFormFromUploadedDocumentContent(prompt) {
  return buildCanvasFromUploadedDocument({ prompt });
}

/**
 * @param {string} prompt
 * @param {{ allowedModes?: string[], currentFields?: object[], formName?: string, formHeader?: string }} [options]
 * @returns {{ fields: object[], code?: string, name?: string, moduleScope?: string, summary: string, blocked?: boolean, replaceCanvas?: boolean }}
 */
export function generateESourceFormFromPrompt(prompt, options = {}) {
  const text = String(prompt || "").trim();
  const allowedModes = normalizeAllowedModes(options.allowedModes);
  const allowClinical = allowedModes.includes("clinicalPacks");
  const allowGeneral = allowedModes.includes("generalForms");
  const canvasReady = hasCanvasFields(options.currentFields);

  if (!text) {
    return {
      fields: [],
      blocked: false,
      replaceCanvas: false,
      summary: canvasReady
        ? "Ask me about this form, or describe a clinical pack to replace it."
        : "Describe a clinical form to generate fields.",
    };
  }

  if (isSmallTalk(text)) {
    return smallTalkReply(
      canvasReady,
      options.currentFields,
      { formName: options.formName, formHeader: options.formHeader },
    );
  }

  // Uploaded PDF / Word → build fields from extracted text (local path).
  if (
    /Document content:/i.test(text) ||
    /Build an e-Source form from this uploaded document/i.test(text)
  ) {
    return buildFormFromUploadedDocumentContent(text);
  }

  // Clinical packs always take priority when matched.
  for (const pack of STARTER_PACKS) {
    if (pack.match.some((re) => re.test(text))) {
      if (!allowClinical) {
        return outOfScopeWarning(
          "Clinical packs are not enabled for your organisation. Ask Super Admin to turn them on.",
        );
      }
      return {
        fields: mapSpecsWithUniqueNames(pack.fields),
        code: pack.code,
        name: pack.name,
        moduleScope: pack.scope,
        blocked: false,
        replaceCanvas: true,
        summary: canvasReady
          ? `Replaced the canvas with the “${pack.name}” pack (${pack.fields.length} fields). Review and edit before saving.`
          : `Suggested “${pack.name}” pack (${pack.fields.length} fields). Review and edit before saving.`,
      };
    }
  }

  const buildingNew = isNewFormBuildRequest(text);
  const generalIntent = matchGeneralIntent(text);

  // Prefer canvas edits/chat — this form is the conversation context.
  if (canvasReady) {
    if (generalIntent && buildingNew) {
      if (!allowGeneral) {
        return outOfScopeWarning(
          "General business forms are not enabled. Stay on this clinical form, or ask Super Admin to enable General forms.",
        );
      }
      const name = generalIntent.nameFrom(text);
      return {
        fields: mapSpecsWithUniqueNames(generalIntent.fields),
        code: generalIntent.code,
        name,
        moduleScope: "General",
        blocked: false,
        replaceCanvas: true,
        summary: `Replaced the canvas with “${name}” (${generalIntent.fields.length} fields).`,
      };
    }
    return handleCanvasAssist(text, {
      ...options,
      allowedModes,
      recentMessages: options.recentMessages,
    });
  }

  // Block only when they clearly ask for a general / business form.
  if (generalIntent && (buildingNew || !canvasReady)) {
    if (!allowGeneral) {
      return outOfScopeWarning(
        "General business forms are not enabled. Use a clinical form, or ask Super Admin to enable General forms.",
      );
    }
    const name = generalIntent.nameFrom(text);
    return {
      fields: mapSpecsWithUniqueNames(generalIntent.fields),
      code: generalIntent.code,
      name,
      moduleScope: "General",
      blocked: false,
      replaceCanvas: true,
      summary: `Generated “${name}” (${generalIntent.fields.length} fields). Review and edit before saving.`,
    };
  }

  const inline = parseInlineFields(text);
  if (inline.length && (buildingNew || !canvasReady)) {
    if (!allowGeneral) {
      return outOfScopeWarning(
        "Custom free-text field lists need General forms. Ask Super Admin to enable them, or use a clinical pack.",
      );
    }
    return {
      fields: inline,
      name: titleCase(
        clip(
          text.replace(/(?:fields?|include|add)\s*:?.+$/i, "").trim() || text,
          60,
        ),
      ),
      code: codeFromTitle(text),
      moduleScope: "General",
      blocked: false,
      replaceCanvas: true,
      summary: `Created ${inline.length} field(s) from your field list. Adjust types and required flags as needed.`,
    };
  }

  // Blank canvas: change/review requests need a form first.
  if (isChangeOrReviewRequest(text) && !buildingNew) {
    return blankCanvasAlert(allowedModes);
  }

  if (buildingNew && !allowGeneral) {
    return outOfScopeWarning(
      `Only clinical forms are enabled. I can build: ${availableFormsBlurb(allowedModes)}.`,
    );
  }

  if (allowGeneral) {
    const generic = buildGenericForm(text);
    return { ...generic, blocked: false, replaceCanvas: true };
  }

  // Helpful chat — not an error alert.
  return {
    fields: [],
    blocked: false,
    replaceCanvas: false,
    summary: `Load a prototype or create a form first. I can build: ${availableFormsBlurb(allowedModes)}.`,
  };
}

function normalizeAllowedModes(raw) {
  const list = Array.isArray(raw) ? raw : [];
  const modes = new Set();
  list.forEach((item) => {
    const token = String(item || "").trim();
    if (/^clinical/i.test(token)) modes.add("clinicalPacks");
    if (/^general/i.test(token) || /^generic/i.test(token)) {
      modes.add("generalForms");
    }
  });
  if (modes.size === 0) modes.add("clinicalPacks");
  return Array.from(modes);
}

function outOfScopeWarning(message) {
  return {
    fields: [],
    blocked: true,
    replaceCanvas: false,
    summary: message,
  };
}

export const ESOURCE_AI_PROMPT_HINTS = [
  "CP05A IMP Accountability Log",
  "SM02A Temperature Monitoring Log",
  "CP03A Biological Sample Processing Log",
  "CP07B Monthly Study Specific IMP Compliance Check",
];

/** Hints when the canvas already has fields (review / incremental edit). */
export const ESOURCE_AI_CANVAS_PROMPT_HINTS = [
  "Review my current form — what are the gaps?",
  "Add a Comments field",
  "Make Notes optional",
  "Suggest improvements for this form",
];

/** Hints when Super Admin has enabled general forms. */
export const ESOURCE_AI_GENERAL_PROMPT_HINTS = [
  "Kirana shop form for taking order from customer",
  "Customer feedback / rating survey",
  "Staff attendance check-in form",
];

export function getEsourceAiPromptHints(
  allowedModes = ["clinicalPacks"],
  { hasCanvasFields: canvasHasFields = false } = {},
) {
  if (canvasHasFields) {
    return ESOURCE_AI_CANVAS_PROMPT_HINTS;
  }
  const modes = normalizeAllowedModes(allowedModes);
  if (modes.includes("generalForms") && modes.includes("clinicalPacks")) {
    return [
      ...ESOURCE_AI_PROMPT_HINTS.slice(0, 2),
      ...ESOURCE_AI_GENERAL_PROMPT_HINTS.slice(0, 2),
    ];
  }
  if (modes.includes("generalForms")) {
    return ESOURCE_AI_GENERAL_PROMPT_HINTS;
  }
  return ESOURCE_AI_PROMPT_HINTS;
}

export function applyReadymadePack(packId) {
  const pack = STARTER_PACKS.find((p) => p.id === packId);
  if (!pack) {
    return {
      fields: [],
      summary: "Template not found.",
    };
  }
  return {
    fields: mapSpecsWithUniqueNames(pack.fields),
    code: pack.code,
    name: pack.name,
    moduleScope: pack.scope,
    summary: `Loaded “${pack.name}” (${pack.fields.length} fields). Edit before saving.`,
  };
}

/** Compact canvas snapshot for Form Builder Azure Ask. */
export function buildCanvasSnapshotForAssist(fields) {
  return (Array.isArray(fields) ? fields : []).map((f, i) => ({
    index: i + 1,
    type: String(f?.type || "text"),
    label: fieldDisplayLabel(f),
    fieldName: String(f?.fieldName || f?.name || "").trim() || undefined,
    required: !!f?.required,
    maxLength: f?.maxLength !== "" && f?.maxLength != null ? f.maxLength : undefined,
    minLength: f?.minLength !== "" && f?.minLength != null ? f.minLength : undefined,
    pattern: f?.pattern ? String(f.pattern) : undefined,
    minValue: f?.minValue !== "" && f?.minValue != null ? f.minValue : undefined,
    maxValue: f?.maxValue !== "" && f?.maxValue != null ? f.maxValue : undefined,
  }));
}

/** Stable fingerprint so we can tell if Assist really changed the canvas. */
export function fingerprintAssistFields(fields) {
  return (Array.isArray(fields) ? fields : []).map((f) => ({
    id: f?.id,
    type: f?.type || "text",
    label: fieldDisplayLabel(f),
    required: !!f?.required,
    maxLength: f?.maxLength ?? "",
    minLength: f?.minLength ?? "",
    pattern: f?.pattern ?? "",
    minValue: f?.minValue ?? "",
    maxValue: f?.maxValue ?? "",
    width: f?.width ?? "",
  }));
}

export function assistCanvasDidChange(beforeFields, afterFields) {
  try {
    return (
      JSON.stringify(fingerprintAssistFields(beforeFields)) !==
      JSON.stringify(fingerprintAssistFields(afterFields))
    );
  } catch {
    return false;
  }
}

/** Short change notes for the chat “Canvas updated” panel. */
export function summarizeAssistFieldChanges(beforeFields, afterFields) {
  const before = Array.isArray(beforeFields) ? beforeFields : [];
  const after = Array.isArray(afterFields) ? afterFields : [];
  const notes = [];
  if (before.length !== after.length) {
    notes.push(`Field count ${before.length} → ${after.length}`);
  }

  const beforeById = new Map(before.map((f) => [f.id, f]));
  const afterIds = new Set(after.map((f) => f.id));

  // Removals / additions first — these are the real intent (not renumber noise).
  before.forEach((f) => {
    if (!afterIds.has(f.id)) {
      notes.push(`Removed “${fieldDisplayLabel(f)}”`);
    }
  });

  after.forEach((f) => {
    if (!beforeById.has(f.id)) {
      notes.push(`Added “${fieldDisplayLabel(f)}” (${f.type})`);
    }
  });

  // Only report “moved” when relative order among surviving fields changed
  // (ignore pure renumbering after add/remove).
  const beforeSharedIds = before
    .filter((f) => afterIds.has(f.id))
    .map((f) => f.id);
  const afterSharedIds = after
    .filter((f) => beforeById.has(f.id))
    .map((f) => f.id);
  const orderChanged =
    beforeSharedIds.length > 1 &&
    beforeSharedIds.join("\0") !== afterSharedIds.join("\0");

  after.forEach((f, i) => {
    const prev = beforeById.get(f.id);
    if (!prev) return;
    const label = fieldDisplayLabel(f);
    const bits = [];
    if (prev.type !== f.type) bits.push(`type ${prev.type}→${f.type}`);
    if (!!prev.required !== !!f.required) {
      bits.push(f.required ? "required" : "optional");
    }
    if (String(prev.label || "") !== String(f.label || "")) {
      bits.push(`renamed to “${label}”`);
    }
    if (String(prev.maxLength ?? "") !== String(f.maxLength ?? "")) {
      bits.push(`maxLength ${f.maxLength}`);
    }
    if (String(prev.minLength ?? "") !== String(f.minLength ?? "")) {
      bits.push(`minLength ${f.minLength}`);
    }
    if (String(prev.pattern ?? "") !== String(f.pattern ?? "")) {
      bits.push("validation pattern");
    }
    if (orderChanged) {
      const prevSharedIdx = beforeSharedIds.indexOf(f.id);
      const nextSharedIdx = afterSharedIds.indexOf(f.id);
      if (prevSharedIdx !== nextSharedIdx) {
        bits.push(`moved to #${i + 1}`);
      }
    }
    if (bits.length) notes.push(`“${label}”: ${bits.join(", ")}`);
  });

  return notes.slice(0, 12);
}

export function looksLikeAssistEditIntent(text) {
  return /\b(add|remove|delete|change|convert|update|set|make|mark|rename|move|swap|insert|put|place|apply|required|optional|type|digit|length|divider|section|spacer|validation|accept)\b/i.test(
    String(text || ""),
  );
}

function normalizeServerFieldSpec(raw) {
  if (!raw || typeof raw !== "object") return null;
  const type = String(raw.type ?? raw.Type ?? "text").trim() || "text";
  const label = String(raw.label ?? raw.Label ?? "").trim();
  if (!label && !isLayoutField(type)) return null;
  return {
    type,
    label: label || typeMetaLabel(type),
    required: !!(raw.required ?? raw.Required),
    width: raw.width ?? raw.Width ?? undefined,
    options: raw.options ?? raw.Options,
  };
}

function typeMetaLabel(type) {
  if (type === "divider") return "Divider";
  if (type === "section") return "Section";
  if (type === "spacer") return "Spacer";
  return "Field";
}

/**
 * Apply Azure / server Form Builder Assist payload to the current canvas.
 */
export function applyFormBuilderServerAssist(serverResult, currentFields = []) {
  const summary =
    String(serverResult?.summary || "").trim() ||
    "Form Assist updated the canvas.";
  if (serverResult?.blocked) {
    return {
      fields: [],
      blocked: true,
      replaceCanvas: false,
      summary,
    };
  }

  let fields = Array.isArray(currentFields)
    ? currentFields.map((f) => ({ ...f }))
    : [];
  const ops = Array.isArray(serverResult?.operations)
    ? serverResult.operations
    : [];
  let touched = false;
  let incremental = !!serverResult?.incremental;

  for (const opRaw of ops) {
    const op = String(opRaw?.op ?? opRaw?.Op ?? "")
      .trim()
      .toLowerCase();
    if (!op) continue;

    if (op === "replaceall") {
      const specs = (opRaw.fields ?? opRaw.Fields ?? [])
        .map(normalizeServerFieldSpec)
        .filter(Boolean);
      fields = mapSpecsWithUniqueNames(specs);
      incremental = false;
      touched = true;
      continue;
    }

    if (op === "add") {
      const spec = normalizeServerFieldSpec(opRaw);
      if (!spec) continue;
      const field = fieldFromSpec(spec, fields);
      const afterIndex = Number(opRaw.afterIndex ?? opRaw.AfterIndex);
      if (Number.isFinite(afterIndex) && afterIndex >= 1 && afterIndex <= fields.length) {
        fields.splice(afterIndex, 0, field);
      } else {
        fields.push(field);
      }
      incremental = true;
      touched = true;
      continue;
    }

    if (op === "remove") {
      const idx = Number(opRaw.index ?? opRaw.Index);
      const label = String(opRaw.label ?? opRaw.Label ?? "").trim();
      if (Number.isFinite(idx) && idx >= 1 && idx <= fields.length) {
        fields.splice(idx - 1, 1);
        incremental = true;
        touched = true;
      } else if (label) {
        const key = label.toLowerCase();
        const next = fields.filter(
          (f) => !fieldDisplayLabel(f).toLowerCase().includes(key),
        );
        if (next.length !== fields.length) {
          fields = next;
          incremental = true;
          touched = true;
        }
      }
      continue;
    }

    if (op === "setrequired") {
      const required = !!(opRaw.required ?? opRaw.Required);
      const idx = Number(opRaw.index ?? opRaw.Index);
      const labelOne = String(opRaw.label ?? opRaw.Label ?? "").trim();
      const labelsRaw = opRaw.labels ?? opRaw.Labels;
      const labels = Array.isArray(labelsRaw)
        ? labelsRaw.map((x) => String(x || "").trim()).filter(Boolean)
        : labelOne
          ? [labelOne]
          : [];

      if (Number.isFinite(idx) && idx >= 1 && idx <= fields.length) {
        const f = fields[idx - 1];
        if (!isLayoutField(f.type)) {
          fields[idx - 1] = { ...f, required };
          incremental = true;
          touched = true;
        }
      } else if (labels.length) {
        fields = fields.map((f) => {
          if (isLayoutField(f.type)) return f;
          const labelKey = normalizeFieldKey(fieldDisplayLabel(f));
          const hit = labels.some((lab) => {
            const target = normalizeFieldKey(lab);
            return (
              target &&
              (labelKey === target ||
                labelKey.includes(target) ||
                target.includes(labelKey))
            );
          });
          if (!hit) return f;
          touched = true;
          incremental = true;
          return { ...f, required };
        });
      }
      continue;
    }

    if (op === "rename") {
      const idx = Number(opRaw.index ?? opRaw.Index);
      const label = String(opRaw.label ?? opRaw.Label ?? "").trim();
      if (label && Number.isFinite(idx) && idx >= 1 && idx <= fields.length) {
        const f = fields[idx - 1];
        fields[idx - 1] = {
          ...f,
          label,
          fieldName: ensureUniqueFieldName(
            slugifyFieldName(label),
            fields.filter((x) => x.id !== f.id),
            f.id,
          ),
        };
        incremental = true;
        touched = true;
      }
      continue;
    }

    if (op === "settype") {
      const idx = Number(opRaw.index ?? opRaw.Index);
      const type = String(opRaw.type ?? opRaw.Type ?? "").trim();
      if (type && Number.isFinite(idx) && idx >= 1 && idx <= fields.length) {
        const f = fields[idx - 1];
        const rebuilt = fieldFromSpec(
          {
            type,
            label: f.label,
            required: !!f.required,
            width: f.width,
            maxLength: f.maxLength,
            minLength: f.minLength,
            pattern: f.pattern,
            minValue: f.minValue,
            maxValue: f.maxValue,
          },
          fields.filter((x) => x.id !== f.id),
        );
        fields[idx - 1] = { ...rebuilt, id: f.id, fieldName: f.fieldName };
        incremental = true;
        touched = true;
      }
      continue;
    }

    if (op === "patch") {
      const idx = Number(opRaw.index ?? opRaw.Index);
      const label = String(opRaw.label ?? opRaw.Label ?? "").trim();
      let targetIndexes = [];
      if (Number.isFinite(idx) && idx >= 1 && idx <= fields.length) {
        targetIndexes = [idx - 1];
      } else if (label) {
        const key = normalizeFieldKey(label);
        fields.forEach((f, i) => {
          const lk = normalizeFieldKey(fieldDisplayLabel(f));
          if (
            key &&
            (lk === key || lk.includes(key) || key.includes(lk))
          ) {
            targetIndexes.push(i);
          }
        });
      }
      if (!targetIndexes.length) continue;

      const type = String(opRaw.type ?? opRaw.Type ?? "").trim();
      const hasRequired = opRaw.required != null || opRaw.Required != null;
      const required = !!(opRaw.required ?? opRaw.Required);
      const patchKeys = [
        "maxLength",
        "minLength",
        "pattern",
        "minValue",
        "maxValue",
        "helpText",
        "placeholder",
      ];
      for (const i of targetIndexes) {
        let f = fields[i];
        if (type && type !== f.type && !isLayoutField(f.type)) {
          const rebuilt = fieldFromSpec(
            {
              type,
              label: f.label,
              required: hasRequired ? required : !!f.required,
              width: f.width,
              maxLength: f.maxLength,
              minLength: f.minLength,
              pattern: f.pattern,
              minValue: f.minValue,
              maxValue: f.maxValue,
            },
            fields.filter((x) => x.id !== f.id),
          );
          f = { ...rebuilt, id: f.id, fieldName: f.fieldName };
        } else if (hasRequired && !isLayoutField(f.type)) {
          f = { ...f, required };
        }
        for (const key of patchKeys) {
          const val = opRaw[key] ?? opRaw[key[0].toUpperCase() + key.slice(1)];
          if (val !== undefined && val !== null && val !== "") {
            f = { ...f, [key]: val };
          }
        }
        fields[i] = f;
        incremental = true;
        touched = true;
      }
      continue;
    }

    if (op === "move") {
      const from = Number(opRaw.fromIndex ?? opRaw.FromIndex);
      const to = Number(opRaw.toIndex ?? opRaw.ToIndex);
      if (
        Number.isFinite(from) &&
        Number.isFinite(to) &&
        from >= 1 &&
        to >= 1 &&
        from <= fields.length &&
        to <= fields.length
      ) {
        const [item] = fields.splice(from - 1, 1);
        fields.splice(to - 1, 0, item);
        incremental = true;
        touched = true;
      }
    }
  }

  const fieldSpecs = Array.isArray(serverResult?.fields)
    ? serverResult.fields.map(normalizeServerFieldSpec).filter(Boolean)
    : [];
  if (!touched && fieldSpecs.length) {
    fields = mapSpecsWithUniqueNames(fieldSpecs);
    incremental = !!serverResult?.incremental;
    touched = true;
  }

  const replaceCanvas =
    serverResult?.replaceCanvas === false
      ? false
      : touched || serverResult?.replaceCanvas === true;

  return {
    fields: replaceCanvas ? fields : [],
    blocked: false,
    replaceCanvas,
    incremental: replaceCanvas ? incremental : false,
    name: serverResult?.name || undefined,
    code: serverResult?.code || undefined,
    moduleScope: serverResult?.moduleScope || undefined,
    summary,
  };
}
